import { type ReactNode } from "react";
import { useEquipmentDraftState } from "./EquipmentDraft";
import { ArticleQuickCreate } from "./ArticleQuickCreate";
import { Button } from "@/components/ui/button";
import { EquipmentScanButton } from "./EquipmentScanner";
import { danas } from "@/lib/dan";
import {
  siteEquipmentApi,
  type EquipmentArticle,
  type EquipmentBalance,
  type EquipmentMovementKind,
  type EquipmentMovementInput,
  type EquipmentPiece,
  type EquipmentPlace,
  type EquipmentPlaceDetail,
  type EquipmentResult,
  type EquipmentTransfer,
  type EquipmentMissing,
} from "@/lib/api/oprema";
import {
  Field,
  Ownership,
  Photos,
  Quantity,
  SaveErrors,
  useEquipmentSave,
  useEquipmentAccess,
  EquipmentRouteButton,
} from "./shared";
import {
  emptyProof,
  HandoverProofFields,
  proofInput,
  proofReady,
  proofMissing,
} from "./HandoverProof";

export type EquipmentSaved = (result: EquipmentResult, message: string) => void;
interface FormProps {
  onSaved: EquipmentSaved;
  onClose: () => void;
  emptyNext?: ReactNode;
}
function EmptyEquipmentForm({
  title,
  message,
  onClose,
  children,
}: {
  title: string;
  message: string;
  onClose: () => void;
  children?: ReactNode;
}) {
  return (
    <section className="equipment-card" aria-label={title}>
      <h2 className="text-xl font-bold">{title}</h2>
      <b>{message}</b>
      {children}
      <Button variant="outline" onClick={onClose}>
        Natrag na opremu
      </Button>
    </section>
  );
}
export interface MovementTarget {
  article: EquipmentArticle;
  balance?: EquipmentBalance;
  piece?: EquipmentPiece;
  missing?: EquipmentMissing;
}
export function targetKey(target: MovementTarget) {
  return (
    target.missing?.kretanje_id ??
    target.piece?.id ??
    `${target.article.id}:${target.balance?.najam_id ?? "own"}`
  );
}
export function placeTargets(data: EquipmentPlaceDetail): MovementTarget[] {
  return [
    ...data.stanje.flatMap((balance) => {
      const article = data.artikli.find((a) => a.id === balance.artikl_id);
      return article ? [{ article, balance }] : [];
    }),
    ...data.komadi.flatMap((piece) => {
      const article = data.artikli.find((a) => a.id === piece.artikl_id);
      return article ? [{ article, piece }] : [];
    }),
  ];
}
function equipmentPayload(target: MovementTarget) {
  return {
    artikl_id: target.article.id,
    komad_id: target.missing
      ? target.missing.komad_id
      : (target.piece?.id ?? null),
    najam_id: target.missing
      ? target.missing.najam_id
      : (target.piece?.najam_id ?? target.balance?.najam_id ?? null),
  };
}
export function missingTargets(
  missing: EquipmentMissing[],
  articles: EquipmentArticle[],
  pieces: EquipmentPiece[],
): MovementTarget[] {
  return missing
    .filter((m) => m.kolicina_preostalo > 0)
    .flatMap((m) => {
      const article = articles.find((a) => a.id === m.artikl_id);
      return article
        ? [
            {
              article,
              missing: m,
              piece: pieces.find((p) => p.id === m.komad_id),
            },
          ]
        : [];
    });
}
function targetName(target: MovementTarget) {
  return `${target.article.naziv}${target.piece ? ` · ${target.piece.inventarni_broj}` : ""}`;
}
function DateInput({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  const today = danas();
  const start = new Date(`${today}T12:00:00Z`);
  start.setUTCDate(start.getUTCDate() - 30);
  return (
    <Field label="Datum" error={error}>
      <input
        type="date"
        value={value}
        min={start.toISOString().slice(0, 10)}
        max={today}
        required
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}
export function TransferForm({
  data,
  places,
  onSaved,
  onClose,
  initialPiece,
  initialTarget,
  emptyNext,
  articles = data.artikli,
  onArticleCreated,
}: FormProps & {
  data: EquipmentPlaceDetail;
  places: EquipmentPlace[];
  initialPiece?: string;
  initialTarget?: string;
  articles?: EquipmentArticle[];
  onArticleCreated?: (article: EquipmentArticle) => void;
}) {
  const access = useEquipmentAccess();
  const [articleOpen, setArticleOpen] = useEquipmentDraftState("articleOpen", false);
  const [created, setCreated] = useEquipmentDraftState<EquipmentArticle[]>("created", []);
  const [search, setSearch] = useEquipmentDraftState("search", "");
  const catalogue = [...articles, ...created.filter((a) => !articles.some((old) => old.id === a.id))];
  const createArticle = <ArticleQuickCreate empty={!catalogue.length} open={articleOpen} onOpenChange={setArticleOpen} onCreated={(article) => {
    setCreated((old) => [...old, article]);
    onArticleCreated?.(article);
  }} />;
  const [proof, setProof] = useEquipmentDraftState("proof", emptyProof);
  const targets = placeTargets(data).filter((t) =>
    t.piece
      ? t.piece.stanje === "ispravno" && !t.piece.ceka_otpis
      : (t.balance?.ispravno ?? 0) > 0,
  );
  const [quantities, setQuantities] = useEquipmentDraftState<
    Record<string, number>
  >(
    "quantities",
    initialTarget
      ? { [initialTarget]: 1 }
      : initialPiece
        ? { [initialPiece]: 1 }
        : {},
  );
  const [destination, setDestination] = useEquipmentDraftState(
    "destination",
    "",
  );
  const [photos, setPhotos] = useEquipmentDraftState<File[]>("photos", []);
  const [date, setDate] = useEquipmentDraftState("date", danas);
  const save = useEquipmentSave(onSaved);
  const selected = targets.filter((t) =>
    Object.hasOwn(quantities, targetKey(t)),
  );
  const [adding, setAdding] = useEquipmentDraftState("adding", false);
  const displayedTargets = selected.length && !adding ? selected : targets;
  const invalid = selected.some((t) => {
    const n = quantities[targetKey(t)]!;
    return (
      !Number.isInteger(n) || n <= 0 || n > (t.piece ? 1 : t.balance!.ispravno)
    );
  });
  const name = places.find((p) => p.id === destination)?.naziv;
  const need = [
    ...(articleOpen ? ["Spremi artikl ili odustani"] : []),
    ...(!selected.length ? ["Odaberi što seliš"] : []),
    ...(invalid ? ["Upiši količinu"] : []),
    ...(!destination ? ["Odaberi kamo"] : []),
    ...(!photos.length &&
    !(access.evidenceException && proof.bez_fotke_razlog?.trim())
      ? ["Dodaj fotku"]
      : []),
    ...(!proof.potpis &&
    !(access.evidenceException && proof.bez_potpisa_razlog?.trim())
      ? ["Dodaj potpis"]
      : []),
  ];
  const blocked = articleOpen ? "Spremi artikl ili odustani" : !selected.length
    ? "Odaberi što seliš"
    : invalid
      ? "Upiši količinu"
      : !destination
        ? "Odaberi kamo"
        : proofMissing(photos, proof, access.evidenceException);
  if (
    targets.length &&
    !places.some((p) => p.aktivno && p.id !== data.mjesto.id)
  )
    return (
      <EmptyEquipmentForm
        title="Preseli"
        message="Nema drugog aktivnog mjesta."
        onClose={onClose}
      >
        <p>Skladištar treba dodati Bazu ili Radionu.</p>
        {access.all && (
          <EquipmentRouteButton to="/oprema/mjesta">
            Otvori Mjesta
          </EquipmentRouteButton>
        )}
      </EmptyEquipmentForm>
    );
  if (!targets.length)
    return (
      <EmptyEquipmentForm
        title="Preseli"
        message={placeTargets(data).some((t) => t.piece ? ["ispravno", "osteceno", "u_radioni"].includes(t.piece.stanje) : t.balance!.ispravno + t.balance!.osteceno + t.balance!.u_radioni > 0) ? "Nema ispravne opreme za preseljenje." : "Na ovom mjestu još nema opreme."}
        onClose={onClose}
      >
        {emptyNext}
        {createArticle}
        {created.length > 0 && <p>Artikl je spremljen. Prvo ga dodaj na mjesto kroz Popiši.</p>}
      </EmptyEquipmentForm>
    );
  return (
    <form
      className="equipment-form"
      aria-label="Preseli"
      onSubmit={(e) => {
        e.preventDefault();
        if (
          articleOpen ||
          !destination ||
          !selected.length ||
          invalid ||
          !proofReady(photos, proof, access.evidenceException)
        )
          return;
        void save.run(
          () =>
            siteEquipmentApi.transfer(
              {
                iz_mjesta_id: data.mjesto.id,
                u_mjesto_id: destination,
                datum: date,
                stavke: selected.map((t) => ({
                  ...equipmentPayload(t),
                  kolicina: quantities[targetKey(t)]!,
                })),
              },
              photos,
              proof.potpis,
              proofInput(photos, proof, access.evidenceException),
            ),
          `Poslano: ${name}. U prijevozu – čeka potvrdu primitka.`,
        );
      }}
    >
      <h2 className="text-lg font-bold">
        Preseli s mjesta {data.mjesto.naziv}
      </h2>
      <fieldset
        className={`equipment-step ${selected.length && !invalid ? "is-done" : ""}`}
      >
        <legend>
          <span className="equipment-step-number">1</span> Što seliš
        </legend>
        <EquipmentScanButton
          onSelect={(piece) => {
            const target = targets.find((item) => item.piece?.id === piece.id);
            if (!target || piece.mjesto_id !== data.mjesto.id)
              throw new Error(
                "Komad nije na ovom mjestu ili nije raspoloživ za selidbu.",
              );
            const key = targetKey(target);
            if (quantities[key])
              throw new Error("Komad je već odabran za selidbu.");
            setQuantities((current) => ({ ...current, [key]: 1 }));
          }}
        />
        {new Set(targets.map((t) => t.article.id)).size > 10 && <Field label="Pretraži artikle"><input type="search" value={search} onChange={(e) => setSearch(e.target.value)} /></Field>}
        {displayedTargets.filter((t) => Object.hasOwn(quantities, targetKey(t)) || t.article.naziv.toLocaleLowerCase("hr").includes(search.toLocaleLowerCase("hr"))).map((target) => {
          const key = targetKey(target);
          const count = quantities[key] ?? 0;
          return (
            <div key={key} className="equipment-transfer-line">
              <div className="equipment-row">
                <Button
                  type="button"
                  variant="outline"
                  aria-pressed={Object.hasOwn(quantities, key)}
                  aria-label={targetName(target)}
                  onClick={() =>
                    setQuantities((q) => {
                      if (!Object.hasOwn(q, key)) return { ...q, [key]: 1 };
                      const next = { ...q };
                      delete next[key];
                      return next;
                    })
                  }
                >
                  <span>
                    {targetName(target)}
                    <small>
                      ima {target.piece ? 1 : target.balance!.ispravno} ispravno
                    </small>
                  </span>
                </Button>
                {!target.piece && Object.hasOwn(quantities, key) && (
                  <Quantity
                    label={`Količina: ${targetName(target)}`}
                    value={count}
                    max={target.balance!.ispravno}
                    error={count > 0 ? save.errors.kolicina : undefined}
                    onChange={(n) => setQuantities((q) => ({ ...q, [key]: n }))}
                  />
                )}
              </div>
              <Ownership item={target.piece ?? target.balance!} />
            </div>
          );
        })}
        {selected.length > 0 && selected.length < targets.length && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setAdding((old) => !old)}
          >
            {adding ? "Završi odabir" : "+ Dodaj još opreme"}
          </Button>
        )}
        {!targets.length && <p>Nema ispravne opreme za selidbu.</p>}
        {createArticle}
        {created.length > 0 && <p>Artikl je spremljen. Prvo ga dodaj na mjesto kroz Popiši.</p>}
        {invalid && (
          <p role="alert">
            Količina mora biti cijeli broj do raspoloživog ispravnog stanja.
          </p>
        )}
      </fieldset>
      <fieldset className={`equipment-step ${destination ? "is-done" : ""}`}>
        <legend>
          <span className="equipment-step-number">2</span> Kamo
        </legend>
        <div className="equipment-choices">
          {places
            .filter((p) => p.aktivno && p.id !== data.mjesto.id)
            .map((place) => (
              <Button
                type="button"
                variant="outline"
                key={place.id}
                aria-pressed={destination === place.id}
                onClick={() => setDestination(place.id)}
              >
                {place.naziv}
              </Button>
            ))}
        </div>
      </fieldset>
      <fieldset
        className={`equipment-step ${proofReady(photos, proof, access.evidenceException) ? "is-done" : ""}`}
      >
        <legend>
          <span className="equipment-step-number">3</span> Fotka i potpis
        </legend>
        {save.errors.u_mjesto_id && (
          <p role="alert" className="equipment-error">
            {save.errors.u_mjesto_id}
          </p>
        )}
        <Photos
          label="Slikaj utovar"
          files={photos}
          onChange={setPhotos}
          required
          error={save.errors.fotke}
        />
        <HandoverProofFields
          value={proof}
          onChange={setProof}
          label="Predao"
          photos={photos}
          allowException={access.evidenceException}
          error={save.errors.potpis}
        />
        <DateInput value={date} onChange={setDate} error={save.errors.datum} />
      </fieldset>
      <SaveErrors
        {...save}
        inline={["kolicina", "u_mjesto_id", "fotke", "datum", "potpis"]}
      />
      <Button type="submit" disabled={save.pending || !!blocked}>
        {save.pending
          ? "Šaljem…"
          : blocked ||
            `Pošalji ${selected.reduce((total, target) => total + quantities[targetKey(target)]!, 0)} kom na: ${name}`}
      </Button>
      {need.length > 0 && (
        <p className="equipment-error" role="status">
          Još treba: {need.join(" · ")}
        </p>
      )}
      <p>
        Do potvrde primitka oprema je „U prijevozu”. Bez potvrde 2 dana slijedi
        podsjetnik.
      </p>
      <Button type="button" variant="outline" onClick={onClose}>
        Odustani
      </Button>
    </form>
  );
}
export function ReceiptForm({
  transfer,
  articles,
  pieces,
  onSaved,
  onClose,
}: FormProps & {
  transfer: EquipmentTransfer;
  articles: EquipmentArticle[];
  pieces: EquipmentPiece[];
}) {
  const access = useEquipmentAccess();
  const [proof, setProof] = useEquipmentDraftState("proof", emptyProof);
  const [quantities, setQuantities] = useEquipmentDraftState(
    "quantities",
    transfer.stavke.map((s) => s.poslano),
  );
  const [reasons, setReasons] = useEquipmentDraftState<Record<number, string>>(
    "reasons",
    {},
  );
  const [photos, setPhotos] = useEquipmentDraftState<File[]>("photos", []);
  const save = useEquipmentSave(onSaved);
  const differences = transfer.stavke.map(
    (s, i) => quantities[i] !== s.poslano,
  );
  const valid =
    transfer.stavke.every(
      (s, i) =>
        Number.isInteger(quantities[i]) &&
        quantities[i]! >= 0 &&
        quantities[i]! <= s.poslano &&
        (!differences[i] || !!reasons[i]),
    ) &&
    (!differences.some(Boolean) || photos.length > 0) &&
    proofReady(photos, proof, access.evidenceException);
  const blocked = transfer.stavke.some(
    (s, i) =>
      !Number.isInteger(quantities[i]) ||
      quantities[i]! < 0 ||
      quantities[i]! > s.poslano,
  )
    ? "Upiši primljenu količinu"
    : differences.some((difference, i) => difference && !reasons[i])
      ? "Odaberi razlog razlike"
      : differences.some(Boolean) && !photos.length
        ? "Dodaj fotku"
        : proofMissing(photos, proof, access.evidenceException);
  return (
    <form
      className="equipment-form"
      aria-label="Potvrdi primitak"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        void save.run(
          () =>
            siteEquipmentApi.receive(
              transfer.id,
              {
                stavke: transfer.stavke.map((s, i) => ({
                  id: s.id,
                  artikl_id: s.artikl_id,
                  komad_id: s.komad_id,
                  najam_id: s.najam_id,
                  primljeno: quantities[i]!,
                  razlika_razlog: differences[i] ? reasons[i]! : null,
                })),
              },
              photos,
              proof.potpis,
              proofInput(photos, proof, access.evidenceException),
            ),
          differences.some(
            (difference, index) =>
              difference && reasons[index] !== "osteceno_u_prijevozu",
          )
            ? "Primitak potvrđen s razlikom. Manjak: Traži se kod pošiljatelja."
            : differences.some(Boolean)
              ? "Primitak potvrđen. Oštećena oprema upisana je ovdje kao oštećena."
              : "Primitak potvrđen.",
        );
      }}
    >
      <h2 className="text-lg font-bold">Stiglo – potvrdi primitak</h2>
      <fieldset className="equipment-step">
        <legend>
          <span className="equipment-step-number">1</span> Koliko je stiglo
        </legend>
        {transfer.stavke.map((item, index) => (
          <div
            className="equipment-card"
            key={`${item.artikl_id}:${item.komad_id}:${item.najam_id}`}
          >
            <b>
              {articles.find((a) => a.id === item.artikl_id)?.naziv ?? "Artikl"}{" "}
              {pieces.find((p) => p.id === item.komad_id)?.inventarni_broj}
            </b>
            <p>Poslano: {item.poslano}</p>
            <Quantity
              label={`Primljeno: ${articles.find((a) => a.id === item.artikl_id)?.naziv ?? index + 1}`}
              value={quantities[index]!}
              max={item.poslano}
              error={save.errors.primljeno}
              onChange={(n) =>
                setQuantities((q) => q.map((old, i) => (i === index ? n : old)))
              }
            />
            {Number.isInteger(quantities[index]) &&
              quantities[index]! >= 0 &&
              quantities[index]! < item.poslano && (
                <>
                  <p className="equipment-warning">
                    Razlika: {item.poslano - quantities[index]!} kom
                  </p>
                  <Field
                    label={`Razlog razlike: ${index + 1}`}
                    error={save.errors.razlika_razlog}
                  >
                    <div className="equipment-choices equipment-receipt-reasons">
                      {(
                        [
                          [
                            "osteceno_u_prijevozu",
                            "Oštećeno u prijevozu (stiglo, ali oštećeno)",
                          ],
                          ["manjak", "Nije stiglo (manjak)"],
                          ["krivi_artikl", "Krivi artikl"],
                        ] as const
                      ).map(([code, label]) => (
                        <Button
                          key={code}
                          type="button"
                          variant="outline"
                          aria-pressed={reasons[index] === code}
                          onClick={() =>
                            setReasons((old) => ({ ...old, [index]: code }))
                          }
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                  </Field>
                  {reasons[index] && (
                    <p role="status">
                      {reasons[index] === "osteceno_u_prijevozu"
                        ? `${item.poslano - quantities[index]!} kom ulazi ovdje kao oštećeno`
                        : `${item.poslano - quantities[index]!} kom ostaje na selidbi kao Traži se dok pošiljatelj ne odabere Pronađeno ili Nestalo`}
                    </p>
                  )}
                </>
              )}
          </div>
        ))}
      </fieldset>
      <fieldset className="equipment-step">
        <legend>
          <span className="equipment-step-number">2</span> Fotka istovara i
          potpis
        </legend>
        <Photos
          files={photos}
          onChange={setPhotos}
          required
          error={save.errors.fotke}
        />
        <HandoverProofFields
          value={proof}
          onChange={setProof}
          label="Primio"
          photos={photos}
          allowException={access.evidenceException}
          error={save.errors.potpis}
        />
      </fieldset>
      <SaveErrors
        {...save}
        inline={["primljeno", "razlika_razlog", "fotke", "potpis"]}
      />
      <Button type="submit" disabled={save.pending || !valid}>
        {save.pending ? "Spremam…" : blocked || "Potvrdi primitak"}
      </Button>
      <Button type="button" variant="outline" onClick={onClose}>
        Odustani
      </Button>
    </form>
  );
}
const movementTitles: Record<EquipmentMovementKind, string> = {
  ostecenje: "Oštećenje",
  u_radionu: "Pošalji u radionu",
  iz_radione: "Vraćeno iz radione",
  produljenje_roka: "Produlji rok",
  otpis: "Otpiši",
  nestalo: "Nestalo",
  pronadeno: "Pronađeno",
  pocetno: "Popiši",
};
export function MovementForm({
  kind,
  targets: suppliedTargets,
  articles,
  hasEquipment,
  onArticleCreated,
  place,
  places,
  onSaved,
  onClose,
  initialTarget,
  emptyNext,
}: FormProps & {
  kind: EquipmentMovementKind;
  targets: MovementTarget[];
  articles?: EquipmentArticle[];
  hasEquipment?: boolean;
  onArticleCreated?: (article: EquipmentArticle) => void;
  place: EquipmentPlace;
  places: EquipmentPlace[];
  initialTarget?: string;
}) {
  const access = useEquipmentAccess();
  const [articleOpen, setArticleOpen] = useEquipmentDraftState("articleOpen", false);
  const [created, setCreated] = useEquipmentDraftState<EquipmentArticle[]>("created", []);
  const [search, setSearch] = useEquipmentDraftState("search", "");
  const targets: MovementTarget[] = kind === "pocetno" ? [...suppliedTargets, ...created.filter((a) => !suppliedTargets.some((t) => t.article.id === a.id)).map((article) => ({article}))] : suppliedTargets;
  const catalogue = articles ?? [...new Map([...suppliedTargets.map((t) => t.article), ...created].map((a) => [a.id, a])).values()];
  const [proof, setProof] = useEquipmentDraftState("proof", emptyProof);
  const handover = kind === "u_radionu" || kind === "iz_radione";
  const [selectedCondition, setSelectedCondition] =
    useEquipmentDraftState<EquipmentMovementInput["stanje"]>(
      "selectedCondition",
    );
  const [targetId, setTargetId] = useEquipmentDraftState(
    "targetId",
    initialTarget ?? (targets.length === 1 ? targetKey(targets[0]!) : ""),
  );
  const [quantity, setQuantity] = useEquipmentDraftState("quantity", () =>
    kind === "pocetno" &&
    targets.length === 1 &&
    targets[0]!.article.nacin === "kolicina"
      ? (targets[0]!.balance?.ispravno ?? 0)
      : 1,
  );
  const [reason, setReason] = useEquipmentDraftState("reason", "");
  const [other, setOther] = useEquipmentDraftState("other", "");
  const [photos, setPhotos] = useEquipmentDraftState<File[]>("photos", []);
  const [date, setDate] = useEquipmentDraftState("date", danas);
  const [deadline, setDeadline] = useEquipmentDraftState("deadline", () => {
    const d = new Date(`${danas()}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [destination, setDestination] = useEquipmentDraftState(
    "destination",
    "",
  );
  const [outcome, setOutcome] = useEquipmentDraftState<
    "popravljeno" | "ne_isplati_se"
  >("outcome", "popravljeno");
  const [cost, setCost] = useEquipmentDraftState("cost", "");
  const [lastSeen, setLastSeen] = useEquipmentDraftState("lastSeen", danas);
  const [policeReport, setPoliceReport] = useEquipmentDraftState(
    "policeReport",
    "",
  );
  const [inventory, setInventory] = useEquipmentDraftState("inventory", "");
  const [serial, setSerial] = useEquipmentDraftState("serial", "");
  const target = targets.find((t) => targetKey(t) === targetId);
  const condition =
    kind === "iz_radione" || kind === "produljenje_roka"
      ? "u_radioni"
      : (selectedCondition ??
        (target?.piece?.stanje === "osteceno" ||
        (target?.balance?.osteceno ?? 0) > 0
          ? "osteceno"
          : kind !== "u_radionu" &&
              (target?.piece?.stanje === "u_radioni" ||
                (target?.balance?.u_radioni ?? 0) > 0)
            ? "u_radioni"
            : "ispravno"));
  const save = useEquipmentSave(onSaved);
  const reduction =
    kind === "pocetno" &&
    target?.article.nacin === "kolicina" &&
    quantity < (target.balance?.ispravno ?? 0);
  const needsPhoto =
    reduction ||
    kind === "ostecenje" ||
    kind === "otpis" ||
    (kind === "iz_radione" && outcome === "ne_isplati_se");
  const needsDestination =
    kind === "u_radionu" ||
    kind === "pronadeno" ||
    (kind === "iz_radione" && outcome === "popravljeno");
  const destinationPlaces = places.filter(
    (p) =>
      p.aktivno &&
      (p.id !== place.id || kind === "pronadeno") &&
      (kind === "u_radionu" ? p.vrsta === "radiona" : p.vrsta !== "radiona"),
  );
  const needsReason =
    reduction ||
    !["pocetno", "iz_radione", "pronadeno"].includes(kind) ||
    (kind === "iz_radione" && outcome === "ne_isplati_se");
  const rented = target?.piece?.najam_id ?? target?.balance?.najam_id;
  const writeOff =
    kind === "otpis" || (kind === "iz_radione" && outcome === "ne_isplati_se");
  const maximum =
    kind === "pronadeno"
      ? (target?.missing?.kolicina_preostalo ?? 0)
      : target?.piece
        ? 1
        : kind === "ostecenje"
          ? target?.balance?.ispravno
          : ["otpis", "nestalo", "u_radionu"].includes(kind)
            ? target?.balance?.[condition]
            : kind === "iz_radione"
              ? target?.balance?.u_radioni
              : undefined;
  function blockedReason() {
    if (articleOpen) return "Spremi artikl ili odustani";
    if (!target)
      return kind === "pocetno"
        ? "Odaberi što popisuješ"
        : "Odaberi artikl ili komad";
    if (kind === "pronadeno" && !target.missing) return "Odaberi zapis Nestalo";
    if (
      !Number.isInteger(quantity) ||
      (kind === "pocetno" && target.article.nacin === "kolicina"
        ? quantity < 0
        : quantity <= 0) ||
      (maximum !== undefined && quantity > maximum)
    )
      return "Upiši količinu";
    if (needsDestination && !destination) return "Odaberi kamo";
    if (needsReason && !reason.trim()) return "Odaberi razlog";
    if (needsReason && reason === "Drugo" && !other.trim()) return "Upiši opis";
    if (rented && writeOff) return "Najam se ne otpisuje";
    if (
      ["u_radionu", "produljenje_roka"].includes(kind) &&
      (deadline < danas() ||
        (kind === "produljenje_roka" &&
          !!target.piece?.rok_povratka &&
          deadline <= target.piece.rok_povratka))
    )
      return "Upiši rok povratka";
    if (needsPhoto && !photos.length) return "Dodaj fotku";
    return handover
      ? proofMissing(photos, proof, access.evidenceException)
      : "";
  }
  const blocked = blockedReason();
  const valid = !blocked;
  const reasons = reduction
    ? ["Manjak", "Krivo upisano ranije", "Drugo"]
    : kind === "ostecenje"
      ? ["Puknuto", "Savijeno", "Ne radi", "Nedostaje dio", "Drugo"]
      : writeOff
        ? ["Dotrajalo", "Uništeno", "Ne isplati se popravak"]
        : [];
  const quickCreate = ["pocetno", "ostecenje"].includes(kind) ? <ArticleQuickCreate empty={!catalogue.length && !created.length} open={articleOpen} onOpenChange={setArticleOpen} onCreated={(article) => {
    setCreated((old) => [...old, article]);
    onArticleCreated?.(article);
    if (kind === "pocetno") {
      setTargetId(targetKey({article})); setQuantity(article.nacin === "kolicina" ? 0 : 1);
      setReason(""); setOther(""); setSelectedCondition(undefined); setSearch("");
    }
  }} /> : null;
  if (!targets.length)
    return (
      <EmptyEquipmentForm
        title={movementTitles[kind]}
        message={
          kind === "ostecenje"
            ? hasEquipment ? "Nema ispravne opreme." : "Na ovom mjestu još nema opreme."
            : kind === "iz_radione" || kind === "produljenje_roka"
              ? "Ništa nije u radioni."
              : kind === "pronadeno"
                ? "Nema neriješene nestale opreme."
                : kind === "pocetno"
                  ? "Dodaj vrstu opreme koju popisuješ."
                  : kind === "u_radionu"
                    ? "Na ovom mjestu još nema opreme za radionu."
                    : "Na ovom mjestu nema opreme."
        }
        onClose={onClose}
      >
        {emptyNext}
        {quickCreate}
        {created.length > 0 && <p>Artikl je spremljen. Prvo ga dodaj na mjesto kroz Popiši.</p>}
      </EmptyEquipmentForm>
    );
  if (needsDestination && !destinationPlaces.length)
    return (
      <EmptyEquipmentForm
        title={movementTitles[kind]}
        message={
          kind === "u_radionu"
            ? "Nema aktivne Radione."
            : "Nema aktivnog odredišta."
        }
        onClose={onClose}
      >
        <p>Skladištar treba dodati ili aktivirati mjesto.</p>
        {access.all && (
          <EquipmentRouteButton to="/oprema/mjesta">
            Otvori Mjesta
          </EquipmentRouteButton>
        )}
      </EmptyEquipmentForm>
    );
  return (
    <form
      className="equipment-form"
      aria-label={movementTitles[kind]}
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !target) return;
        void save.run(
          () =>
            siteEquipmentApi.move(
              {
                vrsta: kind,
                ...equipmentPayload(target),
                mjesto_id: place.id,
                kolicina: target.piece ? 1 : quantity,
                datum: date,
                stanje:
                  kind === "ostecenje" || kind === "pocetno"
                    ? "ispravno"
                    : condition,
                ...(kind === "pronadeno"
                  ? { izvor_kretanje_id: target.missing?.kretanje_id }
                  : {}),
                ...(needsReason
                  ? {
                      razlog:
                        reason === "Drugo" ? `${reason}: ${other}` : reason,
                    }
                  : {}),
                ...(needsDestination ? { u_mjesto_id: destination } : {}),
                ...(["u_radionu", "produljenje_roka"].includes(kind)
                  ? { rok_povratka: deadline }
                  : {}),
                ...(kind === "iz_radione"
                  ? {
                      ishod: outcome,
                      ...(cost ? { iznos_popravka: cost } : {}),
                    }
                  : {}),
                ...(kind === "nestalo"
                  ? {
                      zadnji_put_videno: lastSeen,
                      ...(policeReport
                        ? { broj_prijave_policiji: policeReport }
                        : {}),
                    }
                  : {}),
                ...(kind === "pocetno" && target.article.nacin === "komad"
                  ? {
                      inventarni_broj: inventory || undefined,
                      serijski_broj: serial || undefined,
                    }
                  : {}),
              },
              photos,
              handover ? proof.potpis : null,
              handover
                ? proofInput(photos, proof, access.evidenceException)
                : {},
            ),
          `${movementTitles[kind]} spremljeno.`,
        );
      }}
    >
      <h2 className="text-lg font-bold">{movementTitles[kind]}</h2>
      {kind === "pocetno" && (
        <p>
          Upiši stvarni broj ispravne opreme. Time se zamjenjuje dosadašnji
          broj.
        </p>
      )}
      <fieldset className="equipment-step">
        <legend>
          <span className="equipment-step-number">1</span> Što i koliko
        </legend>
        {new Set(targets.map((t) => t.article.id)).size > 10 && <Field label="Pretraži artikle"><input type="search" value={search} onChange={(e) => setSearch(e.target.value)} /></Field>}
        <Field
          label="Artikl ili komad"
          error={save.errors.artikl_id || save.errors.izvor_kretanje_id}
        >
          <select
            aria-label="Artikl ili komad"
            value={targetId}
            required
            onChange={(e) => {
              if (e.target.value === "__new_article__") { setArticleOpen(true); return; }
              setTargetId(e.target.value);
              const next = targets.find((t) => targetKey(t) === e.target.value);
              setQuantity(
                kind === "pocetno" && next?.article.nacin === "kolicina"
                  ? (next.balance?.ispravno ?? 0)
                  : 1,
              );
              setReason("");
              setOther("");
              setSelectedCondition(undefined);
            }}
          >
            <option value="">Odaberi</option>
            {targets.filter((t) => targetKey(t) === targetId || t.article.naziv.toLocaleLowerCase("hr").includes(search.toLocaleLowerCase("hr"))).map((t) => (
              <option key={targetKey(t)} value={targetKey(t)}>
                {targetName(t)}
                {t.balance?.najam_id || t.piece?.najam_id || t.missing?.najam_id
                  ? " · Najam"
                  : " · Vlastito"}
              </option>
            ))}
            {access.catalogue && ["pocetno", "ostecenje"].includes(kind) && <option value="__new_article__">＋ Novi artikl</option>}
          </select>
        </Field>
        {quickCreate}
        {kind === "ostecenje" && created.length > 0 && <p>Artikl je spremljen. Prvo ga dodaj na mjesto kroz Popiši.</p>}
        {target && (
          <Ownership
            item={
              target.piece ??
              target.balance ?? {
                najam_id: target.missing?.najam_id ?? null,
                najmodavac: null,
                povrat_do: null,
              }
            }
          />
        )}
        {target?.balance &&
          ["u_radionu", "otpis", "nestalo"].includes(kind) && (
            <Field label="Sa stanja">
              <select
                aria-label="Sa stanja"
                value={condition}
                onChange={(e) => {
                  setSelectedCondition(
                    e.target.value as EquipmentMovementInput["stanje"],
                  );
                  setQuantity(1);
                }}
              >
                {(["ispravno", "osteceno", "u_radioni"] as const)
                  .filter(
                    (c) =>
                      target.balance![c] > 0 &&
                      (kind !== "u_radionu" || c !== "u_radioni"),
                  )
                  .map((c) => (
                    <option key={c} value={c}>
                      {
                        {
                          ispravno: "Ispravno",
                          osteceno: "Oštećeno",
                          u_radioni: "U radioni",
                        }[c]
                      }
                    </option>
                  ))}
              </select>
            </Field>
          )}
        {!target?.piece && target?.article.nacin !== "komad" && (
          <Quantity
            label="Količina"
            min={kind === "pocetno" ? 0 : 1}
            max={maximum}
            value={quantity}
            error={save.errors.kolicina}
            onChange={setQuantity}
          />
        )}
        {kind === "pocetno" && target?.article.nacin === "komad" && (
          <>
            <Field label="Inventarni broj (neobavezno)">
              <input
                aria-label="Inventarni broj"
                value={inventory}
                onChange={(e) => setInventory(e.target.value)}
                placeholder="Generira se automatski"
              />
            </Field>
            <Field label="Serijski broj">
              <input
                aria-label="Serijski broj"
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
              />
            </Field>
          </>
        )}
      </fieldset>
      <fieldset className="equipment-step">
        <legend>
          <span className="equipment-step-number">2</span> Upiši podatke
        </legend>
        {kind === "iz_radione" && (
          <>
            <Field label="Ishod">
              <select
                aria-label="Ishod"
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as typeof outcome)}
              >
                <option value="popravljeno">Popravljeno</option>
                <option value="ne_isplati_se">Ne isplati se → otpis</option>
              </select>
            </Field>
            <Field label="Iznos popravka (neobavezno)">
              <input
                aria-label="Iznos popravka"
                inputMode="decimal"
                pattern="\d+(\.\d{1,2})?"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
              />
            </Field>
          </>
        )}
        {needsReason && (
          <Field label="Razlog" error={save.errors.razlog}>
            {reasons.length ? (
              <div className="equipment-choices">
                {reasons.map((r) => (
                  <Button
                    key={r}
                    type="button"
                    variant="outline"
                    aria-pressed={reason === r}
                    onClick={() => setReason(r)}
                  >
                    {r}
                  </Button>
                ))}
              </div>
            ) : (
              <textarea
                aria-label="Razlog"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
        )}
        {needsReason && reason === "Drugo" && (
          <Field
            label={reduction ? "Opis" : "Opiši oštećenje"}
            error={save.errors.opis}
          >
            <textarea
              aria-label={reduction ? "Opis" : "Opiši oštećenje"}
              value={other}
              required
              onChange={(e) => setOther(e.target.value)}
            />
          </Field>
        )}
        {needsDestination && (
          <Field label="Kamo" error={save.errors.u_mjesto_id}>
            <select
              aria-label="Kamo"
              value={destination}
              required
              onChange={(e) => setDestination(e.target.value)}
            >
              <option value="">Odaberi mjesto</option>
              {places
                .filter(
                  (p) =>
                    p.aktivno &&
                    (p.id !== place.id || kind === "pronadeno") &&
                    (kind === "u_radionu"
                      ? p.vrsta === "radiona"
                      : p.vrsta !== "radiona"),
                )
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.naziv}
                  </option>
                ))}
            </select>
          </Field>
        )}
        {["u_radionu", "produljenje_roka"].includes(kind) && (
          <Field label="Rok povratka" error={save.errors.rok_povratka}>
            <input
              aria-label="Rok povratka"
              type="date"
              min={danas()}
              required
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </Field>
        )}
        {kind === "nestalo" && (
          <>
            <Field
              label="Zadnji put viđeno"
              error={save.errors.zadnji_put_videno}
            >
              <input
                aria-label="Zadnji put viđeno"
                type="date"
                max={danas()}
                required
                value={lastSeen}
                onChange={(e) => setLastSeen(e.target.value)}
              />
            </Field>
            <Field label="Broj prijave policiji (neobavezno)">
              <input
                aria-label="Broj prijave policiji"
                value={policeReport}
                onChange={(e) => setPoliceReport(e.target.value)}
              />
            </Field>
          </>
        )}
        <DateInput value={date} onChange={setDate} error={save.errors.datum} />
      </fieldset>
      <fieldset className="equipment-step">
        <legend>
          <span className="equipment-step-number">3</span> Dodaj fotku
          {handover ? " i potpis" : ""}
        </legend>
        <Photos
          files={photos}
          onChange={setPhotos}
          required={needsPhoto || handover}
          error={save.errors.fotke}
        />
        {handover && (
          <HandoverProofFields
            value={proof}
            onChange={setProof}
            label="Predao"
            photos={photos}
            allowException={access.evidenceException}
            error={save.errors.potpis}
          />
        )}
      </fieldset>
      {writeOff && (
        <p className="equipment-warning">
          Za komade i iznose iznad praga otpis čeka potvrdu uprave.
        </p>
      )}
      {rented && writeOff && (
        <p role="alert">
          Oprema u najmu se ne otpisuje, upiši štetu prema najmodavcu.
        </p>
      )}
      <SaveErrors
        {...save}
        inline={[
          "artikl_id",
          "kolicina",
          "razlog",
          "u_mjesto_id",
          "rok_povratka",
          "datum",
          "fotke",
          "potpis",
          "opis",
          "zadnji_put_videno",
          "izvor_kretanje_id",
        ]}
      />
      <Button type="submit" disabled={save.pending || !valid}>
        {save.pending
          ? "Spremam…"
          : blocked ||
            (kind === "ostecenje"
              ? "Prijavi oštećenje"
              : kind === "iz_radione"
                ? "Spremi povratak"
                : movementTitles[kind])}
      </Button>
      <Button type="button" variant="outline" onClick={onClose}>
        Odustani
      </Button>
    </form>
  );
}
